/**
 * StoreContext - Provides memory store access to consumer app
 * v13 Section 8.7 - LangGraph Store Pattern
 *
 * Sprint 13: Updated to depend on Privy wallet + derived encryption keys.
 * Store initializes only after wallet and keys are available.
 * Integrates wallet migration from old mock wallet data.
 *
 * Uses IndexedDBBackend for real persistence in browser.
 * Data persists across page refreshes and is tied to wallet address.
 */

import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import {
  MemoryStore,
  IndexedDBBackend,
  InMemoryBackend,
  MockEmbeddingService,
  type StorageBackend,
} from '@ownyou/memory-store';
import { useAuth } from './AuthContext';
import { getPlatform } from '../utils/platform';
import { WalletMigration } from '../lib/migration/wallet-migration';
import {
  getSlice08SandboxIdentity,
  installSlice08SandboxQaWriter,
} from '../lib/qa/slice08-sandbox';
import { isQaToolsEnabled } from '../lib/wallet/qa-fixtures';

interface StoreContextValue {
  store: MemoryStore | null;
  isReady: boolean;
  error: Error | null;
  /** Backend type for debugging */
  backendType: 'indexeddb' | 'memory' | null;
  /** Encryption key for future Store-level encryption (Sprint 19) */
  storeKey: CryptoKey | null;
  /** Whether migration from mock wallet was performed */
  migrationPerformed: boolean;
}

const StoreContext = createContext<StoreContextValue | null>(null);

interface StoreProviderProps {
  children: ReactNode;
  /** Force in-memory backend (for testing) */
  forceInMemory?: boolean;
}

/**
 * Check if IndexedDB is available and functional
 */
async function isIndexedDBAvailable(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') {
    return false;
  }

  // Test that IndexedDB actually works (some browsers block it in private mode)
  try {
    const testDB = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('__ownyou_idb_test__', 1);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
    testDB.close();
    await new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase('__ownyou_idb_test__');
      deleteRequest.onerror = () => reject(deleteRequest.error);
      deleteRequest.onsuccess = () => resolve();
    });
    return true;
  } catch {
    return false;
  }
}

export function StoreProvider({ children, forceInMemory = false }: StoreProviderProps) {
  const { wallet, isAuthenticated, keys } = useAuth();
  const [store, setStore] = useState<MemoryStore | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [backendType, setBackendType] = useState<'indexeddb' | 'memory' | null>(null);
  const [migrationPerformed, setMigrationPerformed] = useState(false);
  const storeRef = useRef<MemoryStore | null>(null);
  const qaIdentity = getSlice08SandboxIdentity();
  const storeWalletAddress = wallet?.address ?? qaIdentity?.walletAddress ?? null;
  const storeAuthenticated = isAuthenticated || Boolean(qaIdentity);
  const storeKeysReady = Boolean(keys || forceInMemory || qaIdentity);

  useEffect(() => {
    // Wait for wallet AND keys before initializing store.
    // Keys are derived from the wallet signature — needed for future encryption.
    if (!storeAuthenticated || !storeWalletAddress || !storeKeysReady) {
      // Cleanup existing store when logging out
      if (storeRef.current) {
        storeRef.current = null;
      }
      setStore(null);
      setIsReady(false);
      setBackendType(null);
      return;
    }

    const walletAddress = storeWalletAddress;

    const initStore = async () => {
      try {
        // Run migration checks before initializing the new store
        if (!forceInMemory) {
          await runLegacyDbMigration(walletAddress);
          await runMigrationIfNeeded(walletAddress);
        }

        let backend: StorageBackend;
        let type: 'indexeddb' | 'memory';

        const platform = getPlatform();

        // Determine backend based on platform and availability
        if (forceInMemory) {
          // Testing mode - use in-memory
          backend = new InMemoryBackend();
          type = 'memory';
        } else if (platform === 'tauri') {
          // Tauri: Use IndexedDB for now (SQLite requires Tauri plugin)
          // TODO: Implement SQLiteBackend with tauri-plugin-sql
          const idbAvailable = await isIndexedDBAvailable();
          if (idbAvailable) {
            backend = new IndexedDBBackend({
              dbName: `ownyou_${walletAddress.slice(0, 8)}`,
            });
            type = 'indexeddb';
          } else {
            console.warn('IndexedDB not available in Tauri, falling back to memory');
            backend = new InMemoryBackend();
            type = 'memory';
          }
        } else {
          // Browser/PWA: Use IndexedDB for persistence
          const idbAvailable = await isIndexedDBAvailable();
          if (idbAvailable) {
            backend = new IndexedDBBackend({
              dbName: `ownyou_${walletAddress.slice(0, 8)}`,
            });
            type = 'indexeddb';
          } else {
            // Private browsing or IndexedDB blocked
            console.warn('IndexedDB not available, data will not persist across sessions');
            backend = new InMemoryBackend();
            type = 'memory';
          }
        }

        const memoryStore = new MemoryStore({
          backend,
          embeddingService: new MockEmbeddingService(), // TODO: Real embedding service
          // TODO Sprint 19: Pass keys.storeKey for at-rest encryption
        });

        storeRef.current = memoryStore;
        setStore(memoryStore);
        setBackendType(type);
        setIsReady(true);
        setError(null);

        // In dev/test mode only — expose store for E2E test seeding
        if (isQaToolsEnabled()) {
          (window as any).__TEST_STORE = memoryStore;
        }
        installSlice08SandboxQaWriter(memoryStore);

        if (type === 'indexeddb') {
          console.log(`Store initialized with IndexedDB: ownyou_${walletAddress.slice(0, 8)}`);
        }
      } catch (err) {
        console.error('Failed to initialize store:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize store'));
        setIsReady(false);
        setBackendType(null);
      }
    };

    initStore();

    // Cleanup on unmount or wallet change
    return () => {
      if (storeRef.current) {
        // Close IndexedDB connection if applicable
        storeRef.current = null;
      }
    };
  }, [storeAuthenticated, storeWalletAddress, storeKeysReady, forceInMemory]);

  /**
   * One-time migration: copy data from a known legacy wallet DB to the current wallet DB.
   * This handles the case where a Privy SDK update changed the wallet address for the same login.
   */
  const runLegacyDbMigration = async (currentAddress: string) => {
    const MIGRATION_KEY = 'ownyou_legacy_db_migration_done';
    const currentPrefix = currentAddress.slice(0, 8);

    // Only run once
    if (localStorage.getItem(MIGRATION_KEY) === currentPrefix) return;

    // Known legacy DB prefixes to check (excludes current)
    const knownPrefixes = ['0x9F4123', '0xbe3543'].filter(p => p !== currentPrefix);

    for (const legacyPrefix of knownPrefixes) {
      try {
        const legacyDbName = `ownyou_${legacyPrefix}`;
        const currentDbName = `ownyou_${currentPrefix}`;

        // Check if legacy DB exists and has data
        const legacyDb = await new Promise<IDBDatabase>((resolve, reject) => {
          const req = indexedDB.open(legacyDbName);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        });

        const storeNames = [...legacyDb.objectStoreNames];
        if (!storeNames.includes('memories')) {
          legacyDb.close();
          continue;
        }

        // Read all records from legacy DB
        const records = await new Promise<any[]>((resolve, reject) => {
          const tx = legacyDb.transaction('memories', 'readonly');
          const store = tx.objectStore('memories');
          const req = store.getAll();
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        });
        legacyDb.close();

        if (records.length === 0) continue;

        // Check if current DB already has data (don't overwrite)
        const currentDb = await new Promise<IDBDatabase>((resolve, reject) => {
          const req = indexedDB.open(currentDbName);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        });

        if (![...currentDb.objectStoreNames].includes('memories')) {
          currentDb.close();
          continue;
        }

        const currentCount = await new Promise<number>((resolve, reject) => {
          const tx = currentDb.transaction('memories', 'readonly');
          const req = tx.objectStore('memories').count();
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        });

        if (currentCount > 5) {
          // Current DB already has meaningful data, skip
          currentDb.close();
          continue;
        }

        // Find the old userId from records
        const oldUserId = records[0].userId;

        // Copy records, re-keying userId to current wallet address
        const tx = currentDb.transaction('memories', 'readwrite');
        const store = tx.objectStore('memories');
        let migrated = 0;

        for (const record of records) {
          const newKey = record.key.replace(oldUserId, currentAddress);
          store.put({
            ...record,
            key: newKey,
            userId: currentAddress,
          });
          migrated++;
        }

        await new Promise<void>((resolve, reject) => {
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
        currentDb.close();

        console.log(`[StoreContext] Legacy migration: copied ${migrated} records from ${legacyDbName} to ${currentDbName}`);
        break; // Only migrate from one source
      } catch (err) {
        console.warn(`[StoreContext] Legacy migration from ownyou_${legacyPrefix} failed (non-blocking):`, err);
      }
    }

    localStorage.setItem(MIGRATION_KEY, currentPrefix);
  };

  /**
   * Check for old mock wallet data and migrate to the new Privy wallet namespace.
   * Only runs once per wallet address (migration is marked done in localStorage).
   */
  const runMigrationIfNeeded = async (realAddress: string) => {
    try {
      const migration = new WalletMigration();
      const { found, mockAddress } = migration.detectMockWalletData();

      if (!found || !mockAddress) return;

      console.log('[StoreContext] Mock wallet data detected, migrating...');
      const result = await migration.migrateData(mockAddress, realAddress);

      if (result.success) {
        console.log(`[StoreContext] Migration complete: ${result.itemsMigrated} items migrated`);
        if (result.itemsMigrated > 0) {
          await migration.cleanupMockData(mockAddress);
          console.log('[StoreContext] Old mock wallet data cleaned up');
        }
        setMigrationPerformed(true);
      } else {
        console.error('[StoreContext] Migration failed:', result.error);
      }
    } catch (err) {
      // Migration failure should not block store initialization
      console.error('[StoreContext] Migration error (non-blocking):', err);
    }
  };

  return (
    <StoreContext.Provider value={{
      store,
      isReady,
      error,
      backendType,
      storeKey: keys?.storeKey ?? null,
      migrationPerformed,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}

/**
 * Hook to ensure store is ready before operations
 */
export function useStoreReady() {
  const { store, isReady, error } = useStore();

  if (error) {
    throw error;
  }

  if (!isReady || !store) {
    return null;
  }

  return store;
}
